/**
 * Agreement renderer for the Storyblok Visual Editor preview.
 *
 * Mirrors the mobile-app agreement document design:
 *   - Light page background
 *   - White card with rounded corners + soft drop shadow
 *   - Bold header title
 *   - Scrollable markdown body (legal text)
 *
 * Three entrypoints depending on which story the editor opened:
 *   - <AgreementPagePreview>     : story.component === 'content_page'    → list of agreement items
 *   - <AgreementCategoryPreview> : story.component === 'content_category' → single category view
 *   - <AgreementItemPreview>     : story.component === 'agreement_item'   → single agreement document
 */

import React from 'react';
import { Box, Alert, Typography } from '@mui/material';
import { storyblokEditable, renderRichText } from '@storyblok/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { extractHtmlDocument, withPreviewDocumentStyles } from './htmlPreview';

// ── Theme tokens ─────────────────────────────────────────────────────
const COLORS = {
  overlay: 'rgba(0, 0, 0, 0.55)',
  cardBg: '#FFFFFF',
  headerText: '#0D1C3D',
  bodyText: '#4A546A',
  divider: '#E8E6E1',
  btnBg: '#1E7FD9',
  btnText: '#FFFFFF',
};

const CARD_RADIUS = '16px';

// ── Types ─────────────────────────────────────────────────────────────

interface StoryblokRel {
  _uid?: string;
  _editable?: string;
}

interface AgreementItemBlok extends StoryblokRel {
  component: 'agreement_item';
  header?: string;
  body?: string | Record<string, unknown>; // Storyblok richtext object or plain string
}

interface ResolvedItem {
  uuid?: string;
  content?: AgreementItemBlok;
}

interface CategoryBlok extends StoryblokRel {
  component: 'content_category';
  category_name?: string;
  items?: ResolvedItem[] | string[];
}

interface ResolvedCategory {
  uuid?: string;
  content?: CategoryBlok;
}

interface ContentPageBlok extends StoryblokRel {
  component: 'content_page';
  categories?: ResolvedCategory[] | string[];
}

// ── Helpers ──────────────────────────────────────────────────────────

function isResolved<T extends object>(x: unknown): x is T {
  return typeof x === 'object' && x !== null;
}

function editableProps(blok: StoryblokRel | undefined): Record<string, string> {
  if (!blok) return {};
  return storyblokEditable(blok as unknown as Parameters<typeof storyblokEditable>[0]) as Record<string, string>;
}

interface MdProps { children?: React.ReactNode; href?: string; }

function MarkdownBody({ text }: { text: string | Record<string, unknown> | undefined | null }) {
  if (!text) return null;
  const htmlDocument = extractHtmlDocument(text);

  if (htmlDocument) {
    return (
      <Box sx={{ width: '100%', height: 520, overflow: 'hidden' }}>
        <iframe
          srcDoc={withPreviewDocumentStyles(htmlDocument)}
          title="agreement body"
          sandbox="allow-scripts"
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        />
      </Box>
    );
  }

  // If body is a Storyblok richtext object, render as HTML
  if (typeof text === 'object') {
    const html = renderRichText(text as unknown as Parameters<typeof renderRichText>[0]);
    return (
      <div
        style={{ fontSize: 14, color: COLORS.bodyText, lineHeight: 1.7 }}
        dangerouslySetInnerHTML={{ __html: String(html ?? '') }}
      />
    );
  }

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }: MdProps) => (
          <Box component="p" sx={{ m: 0, '& + p': { mt: 1.5 }, fontSize: 14, color: COLORS.bodyText, lineHeight: 1.7 }}>
            {children}
          </Box>
        ),
        h1: ({ children }: MdProps) => (
          <Box component="h1" sx={{ m: 0, mb: 1.5, fontSize: 18, fontWeight: 700, color: COLORS.headerText }}>
            {children}
          </Box>
        ),
        h2: ({ children }: MdProps) => (
          <Box component="h2" sx={{ m: 0, mb: 1, mt: 2, fontSize: 16, fontWeight: 700, color: COLORS.headerText }}>
            {children}
          </Box>
        ),
        h3: ({ children }: MdProps) => (
          <Box component="h3" sx={{ m: 0, mb: 0.75, mt: 1.5, fontSize: 14, fontWeight: 700, color: COLORS.headerText }}>
            {children}
          </Box>
        ),
        ul: ({ children }: MdProps) => (
          <Box component="ul" sx={{ m: 0, mt: 1, mb: 1, pl: 3, fontSize: 14, color: COLORS.bodyText, lineHeight: 1.7 }}>
            {children}
          </Box>
        ),
        ol: ({ children }: MdProps) => (
          <Box component="ol" sx={{ m: 0, mt: 1, mb: 1, pl: 3, fontSize: 14, color: COLORS.bodyText, lineHeight: 1.7 }}>
            {children}
          </Box>
        ),
        a: ({ href, children }: MdProps) => (
          <a href={href} target="_blank" rel="noreferrer" style={{ color: COLORS.headerText }}>
            {children}
          </a>
        ),
        hr: () => (
          <Box component="hr" sx={{ my: 2, border: 0, borderTop: `1px solid ${COLORS.divider}` }} />
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

// ── Modal-style agreement document (matches app UI) ───────────────────

function AgreementDocument({ blok }: { blok: AgreementItemBlok }) {
  return (
    /* Dark overlay backdrop */
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: COLORS.overlay,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      {/* Modal card */}
      <Box
        {...editableProps(blok)}
        sx={{
          bgcolor: COLORS.cardBg,
          borderRadius: CARD_RADIUS,
          width: '100%',
          maxWidth: 440,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Close button (decorative — matches app chrome) */}
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 14,
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'default',
            fontSize: 18,
            color: COLORS.headerText,
            fontWeight: 300,
            lineHeight: 1,
          }}
        >
          ✕
        </Box>

        {/* Scrollable content area */}
        <Box sx={{ overflowY: 'auto', p: 3, pb: 2, flex: 1 }}>
          {/* Header — bold, centred */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 16,
              color: COLORS.headerText,
              lineHeight: 1.4,
              textAlign: 'center',
              mb: 2,
              pr: 3, // avoid overlap with close button
            }}
          >
            {blok.header || <em style={{ opacity: 0.5, fontWeight: 400 }}>(no header set)</em>}
          </Typography>

          {/* Body */}
          {blok.body ? (
            <MarkdownBody text={blok.body} />
          ) : (
            <Typography sx={{ fontSize: 14, color: COLORS.bodyText, fontStyle: 'italic', textAlign: 'center' }}>
              (no body text set)
            </Typography>
          )}
        </Box>

        {/* Done button */}
        <Box sx={{ p: 2, pt: 1 }}>
          <Box
            sx={{
              bgcolor: COLORS.btnBg,
              color: COLORS.btnText,
              borderRadius: 99,
              py: 1.75,
              textAlign: 'center',
              fontWeight: 600,
              fontSize: 16,
              cursor: 'default',
              userSelect: 'none',
            }}
          >
            Done
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ── Page-level preview ────────────────────────────────────────────────

export interface AgreementPagePreviewProps {
  pageBlok: ContentPageBlok;
}

export function AgreementPagePreview({ pageBlok }: AgreementPagePreviewProps) {
  const categories = (pageBlok.categories ?? []) as Array<ResolvedCategory | string>;
  const resolved = categories.filter((c): c is ResolvedCategory => isResolved<ResolvedCategory>(c));
  const unresolvedCount = categories.length - resolved.length;

  const allItems: AgreementItemBlok[] = resolved.flatMap((cat) => {
    const items = (cat.content?.items ?? []) as Array<ResolvedItem | string>;
    return items
      .filter((it): it is ResolvedItem => isResolved<ResolvedItem>(it))
      .map((it) => it.content)
      .filter((c): c is AgreementItemBlok => !!c);
  });

  if (unresolvedCount > 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          {unresolvedCount} item{unresolvedCount > 1 ? 's' : ''} not resolved — ensure{' '}
          <code>resolve_relations=content_page.categories,content_category.items</code>.
        </Alert>
      </Box>
    );
  }

  if (allItems.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          No agreement items yet. Add an <code>agreement_item</code> story inside a category folder.
        </Alert>
      </Box>
    );
  }

  // Show the first item as the modal preview
  return <AgreementDocument blok={allItems[0]} />;
}

// ── Category-level preview ────────────────────────────────────────────

export interface AgreementCategoryPreviewProps {
  categoryBlok: CategoryBlok;
}

export function AgreementCategoryPreview({ categoryBlok }: AgreementCategoryPreviewProps) {
  const items = (categoryBlok.items ?? []) as Array<ResolvedItem | string>;
  const agreementItems: AgreementItemBlok[] = items
    .filter((it): it is ResolvedItem => isResolved<ResolvedItem>(it))
    .map((it) => it.content)
    .filter((c): c is AgreementItemBlok => !!c);

  if (agreementItems.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">No agreement items in this category.</Alert>
      </Box>
    );
  }

  return <AgreementDocument blok={agreementItems[0]} />;
}

// ── Single agreement_item preview ─────────────────────────────────────

export interface AgreementItemPreviewProps {
  itemBlok: AgreementItemBlok;
}

export function AgreementItemPreview({ itemBlok }: AgreementItemPreviewProps) {
  return <AgreementDocument blok={itemBlok} />;
}
